import { ACTIONS } from '~publish/legacy/ai/components/AIAssistant/AIAssistant.constants'
import { getContentGenerationMutation } from '~publish/legacy/ai/state/mutations'
import type {
  TContentGenerationMutationInput,
  TContentGenerationMutationResponse,
} from '~publish/legacy/ai/state/mutations'
import {
  contentGenerationStart,
  contentGenerationSuccess,
  contentGenerationError,
  setContentGenerationConfig,
  selectContentGenerationSelection,
  selectAIAssistantPlacement,
} from '~publish/legacy/ai/state/slice'
import { client } from '~publish/legacy/apollo-client'
import { selectCurrentOrganizationId } from '~publish/legacy/organizations/selectors'
import { useAppSelector, useAppDispatch } from '~publish/legacy/store'

type ContentGenerationAction = keyof typeof ACTIONS
type ContentGenerationMutationName =
  | (typeof ACTIONS)[ContentGenerationAction]['mutation']
  | 'generateContent'
type ContentGenerationMutationData = Partial<
  Record<ContentGenerationMutationName, TContentGenerationMutationResponse>
>

type TGenerateTextParams = {
  prompt: string
  action: string
  service: string | null
  tone: string | null
  previousResult?: string | null
  retry?: boolean
}

// transform enum ErrorCodes in an array of strings
const handledErrors = new Set([
  'LengthError',
  'Timeout',
  'InsufficientTokens',
  'ServiceError',
  'PromptError',
])

const ERROR_MESSAGE_DEFAULT =
  'Whoops! We are unable to generate content at this time. Please try again.'

const isContentGenerationAction = (
  action: string,
): action is ContentGenerationAction => Object.hasOwn(ACTIONS, action)

/**
 *
 */
function useContentGeneration(): {
  generateContent: ({ action }: TGenerateTextParams) => Promise<void>
} {
  const organizationId = useAppSelector((state) =>
    selectCurrentOrganizationId(state),
  )

  const contentGenerationSelection = useAppSelector((state) =>
    selectContentGenerationSelection(state),
  )

  const placement = useAppSelector((state) => selectAIAssistantPlacement(state))

  const dispatch = useAppDispatch()

  const generateContent = async ({
    prompt,
    action,
    tone,
    service,
    previousResult,
    retry,
  }: TGenerateTextParams): Promise<void> => {
    const mutationName: ContentGenerationMutationName =
      isContentGenerationAction(action)
        ? ACTIONS[action].mutation
        : 'generateContent'
    const requestId = Date.now().toString()
    const serviceValue =
      service && !contentGenerationSelection ? service : undefined

    dispatch(setContentGenerationConfig({ action, prompt, tone }))

    dispatch(contentGenerationStart({ requestId }))

    const status = retry ? 'Retry' : 'First'
    const cta = `publish-${placement}-${action}-${status}-1`

    await client
      .mutate<ContentGenerationMutationData, TContentGenerationMutationInput>({
        mutation: getContentGenerationMutation(mutationName),
        variables: {
          organizationId,
          config: {
            prompt,
            cta,
            service: serviceValue,
            tone: tone || undefined,
            previousGeneration: previousResult || undefined,
          },
        },
      })
      .then((response) => {
        const payload = response.data?.[mutationName]
        const suggestions =
          payload && 'suggestions' in payload ? (payload.suggestions ?? []) : []
        const code = payload?.code
        const errorMessage =
          payload && 'error' in payload
            ? (payload.error ?? ERROR_MESSAGE_DEFAULT)
            : ERROR_MESSAGE_DEFAULT

        // An error happened
        if (code) {
          if (handledErrors.has(code)) {
            dispatch(contentGenerationError({ error: errorMessage, requestId }))
          } else {
            dispatch(
              contentGenerationError({
                error: ERROR_MESSAGE_DEFAULT,
                requestId,
              }),
            )
          }
          return undefined
        }

        const content = suggestions[0]?.content || ''
        dispatch(contentGenerationSuccess({ result: content, requestId }))
        return undefined
      })
      .catch(() => {
        dispatch(
          contentGenerationError({ error: ERROR_MESSAGE_DEFAULT, requestId }),
        )
      })
  }

  return {
    generateContent,
  }
}

export { useContentGeneration }
